import React from "react";
import ReactDOM from "react-dom";
import {
  CaretUpFilled,
  CaretDownFilled,
  SearchOutlined,
  AddCircleOutlined,
  TickSmallOutlined,
  MinusOutlined,
  CloseOutlined,
} from "@toddle-edu/ds-icons";
import {
  Button,
  ChecklistDropdown,
  Dropdown,
  DropdownMenu,
  Tooltip,
} from "@toddle-edu/ds-web";
import classes from "./MultiSelectDropdown.scss";
import classNames from "classnames";
import _ from "lodash";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC/I18nHOC";

const styles = {
  overlayInnerStyle: {
    maxWidth: "300px",
    padding: "8px 12px",
    wordBreak: "break-word",
  },
};

// Using our own compose function to avoid dependency cycle
const compose = (...funcs) => {
  if (funcs.length === 0) {
    return arg => arg;
  }

  if (funcs.length === 1) {
    return funcs[0];
  }

  return funcs.reduce((a, b) => (...args) => a(b(...args)));
};

// Local utility function to avoid dependency cycle
const getLabelValueForDropdown = ({
  selectedItems,
  itemList,
  t,
  noneSelectedText,
  showLabelsCount = 1,
  separator = ",",
  showAllText = true,
  moreLabel = "common:more",
}) => {
  const labelsOfSelectedItems = _.map(selectedItems, value => {
    const currentItemObj = _.find(itemList, item => item.value === value);
    return currentItemObj?.label;
  });
  const itemListSize = _.size(itemList);
  const selectedItemsSize = _.size(selectedItems);

  if (_.isEmpty(selectedItems)) {
    return noneSelectedText;
  } else if (selectedItemsSize === 1) {
    return _.first(labelsOfSelectedItems);
  } else if (selectedItemsSize === itemListSize && showAllText) {
    return t("common:all");
  } else {
    const labels = _.slice(labelsOfSelectedItems, 0, showLabelsCount).join(
      ", "
    );

    const count = selectedItemsSize - showLabelsCount;
    const moreText = count !== 0 ? `${separator} ${count} ${t(moreLabel)}` : "";

    return `${labels} ${moreText}`;
  }
};

class MultiSelectDropdown extends UIBaseComponent {
  // Define standard variants that encapsulate common configurations
  static Variants = {
    CHECKBOX_MULTI: "checkbox_multi", // Multi-select with checkboxes
    TAGS_MULTI: "tags_multi", // Multi-select with tags
    SEARCHABLE_MULTI: "searchable_multi", // Multi-select with search
    CREATABLE_MULTI: "creatable_multi", // Multi-select with ability to create options
    SIMPLE_SINGLE: "simple_single", // Single select dropdown
    OBJECT_VALUE_TAGS: "object_value_tags", // Multi-select that works with full objects instead of just IDs
  };

  constructor(props) {
    super(props);

    // Ensure we always start with an array
    let initialValue = props.value || [];

    // Convert string value to array if needed (already handled by above, but keeping for safety)
    if (_.isString(initialValue)) {
      initialValue = [initialValue];
    }
    const config = this.getEffectiveConfig();
    // If in RelatedConcepts mode, store the full objects but extract IDs for internal value
    if (config.features.storeFullObjects && !_.isEmpty(initialValue)) {
      initialValue = _.map(initialValue, "value");
    }
    this.state = {
      ...this.state,
      isOpen: false,
      searchTerm: "",
      options: props.options || [],
      value: initialValue,
      originalValues: config.features.storeFullObjects ? props.value : null,
      buttonWidth: 0,
      dropdownPosition: {
        top: 0,
        left: 0,
        width: 0,
        maxHeight: 300,
        isPositionedAbove: false,
      },
    };
    this.dropdownRef = React.createRef();
    this.inputRef = React.createRef();
    this.buttonRef = React.createRef();
    this.portalDropdownRef = React.createRef();
  }

  getEffectiveConfig() {
    const {
      variant,
      // Legacy props
      storeObjectValues,
      multi,
      showTag,
      isCreatable,
      isSearchable,
      showCheckbox,
      isSelectAllEnabled,
      hideDropdownIndicator,
      // New structure
      features = {},
    } = this.props;

    // Start with default configuration
    let config = {
      mode: "single",
      display: "simple",
      features: {
        searchable: false,
        creatable: false,
        selectAll: true,
        showCheckbox: true,
        hideIndicator: false,
        storeFullObjects: false,
      },
    };

    // If using variant API, apply variant configuration
    if (variant) {
      switch (variant) {
        case MultiSelectDropdown.Variants.CHECKBOX_MULTI:
          config = {
            mode: "multi",
            display: "checkbox",
            features: {
              searchable: false,
              creatable: false,
              selectAll: true,
              showCheckbox: true,
              hideIndicator: true,
              storeFullObjects: false,
            },
          };
          break;

        case MultiSelectDropdown.Variants.TAGS_MULTI:
          config = {
            mode: "multi",
            display: "tags",
            features: {
              searchable: true,
              creatable: false,
              selectAll: true,
              showCheckbox: false,
              hideIndicator: true,
              storeFullObjects: false,
            },
          };
          break;

        case MultiSelectDropdown.Variants.SEARCHABLE_MULTI:
          config = {
            mode: "multi",
            display: "checkbox",
            features: {
              searchable: true,
              creatable: false,
              selectAll: true,
              showCheckbox: true,
              hideIndicator: true,
              storeFullObjects: false,
            },
          };
          break;

        case MultiSelectDropdown.Variants.CREATABLE_MULTI:
          config = {
            mode: "multi",
            display: "tags",
            features: {
              searchable: true,
              creatable: true,
              selectAll: true,
              showCheckbox: false,
              hideIndicator: true,
              storeFullObjects: false,
            },
          };
          break;

        case MultiSelectDropdown.Variants.SIMPLE_SINGLE:
          config = {
            mode: "single",
            display: "simple",
            features: {
              searchable: false,
              creatable: false,
              selectAll: false,
              showCheckbox: false,
              hideIndicator: false,
              storeFullObjects: false,
            },
          };
          break;

        case MultiSelectDropdown.Variants.OBJECT_VALUE_TAGS:
          config = {
            mode: "multi", // No longer using 'relatedConcepts' mode name
            display: "tags",
            features: {
              searchable: true,
              creatable: true,
              selectAll: false,
              showCheckbox: false,
              hideIndicator: true,
              storeFullObjects: true, // This feature flag indicates object value storage
            },
          };
          break;
      }

      // Override variant defaults with any specific feature settings
      config.features = {
        ...config.features,
        ...features,
      };
    }
    // Otherwise, build config from legacy boolean props
    else {
      // Support both legacy isRelatedConceptsMode and new storeObjectValues props
      const useObjectStorage = storeObjectValues;

      config = {
        mode: useObjectStorage ? "multi" : multi ? "multi" : "single",
        display: showTag ? "tags" : showCheckbox ? "checkbox" : "simple",
        features: {
          searchable: isSearchable,
          creatable: isCreatable,
          selectAll: isSelectAllEnabled,
          showCheckbox: showCheckbox,
          hideIndicator: hideDropdownIndicator,
          storeFullObjects: useObjectStorage,
        },
      };
    }

    return config;
  }
  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
    window.addEventListener("scroll", this.updateDropdownPosition, true);
    window.addEventListener("resize", this.updateDropdownPosition);
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    document.removeEventListener("mousedown", this.handleClickOutside);
    window.removeEventListener("scroll", this.updateDropdownPosition, true);
    window.removeEventListener("resize", this.updateDropdownPosition);
  }

  updateDropdownPosition = event => {
    if (
      this.portalDropdownRef.current &&
      event?.target &&
      this.portalDropdownRef.current.contains(event.target)
    ) {
      return;
    }
    if (this.buttonRef.current) {
      const rect = this.buttonRef.current.getBoundingClientRect();
      const dropdownHeight =
        this.portalDropdownRef.current &&
        this.portalDropdownRef.current.getBoundingClientRect()?.height;
      const DROPDOWN_MAX_HEIGHT = 300;
      const GAP = 4;

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const isPositionedAbove =
        spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      const maxHeight = DROPDOWN_MAX_HEIGHT;

      let top;
      if (isPositionedAbove) {
        const idealTop = rect.top + window.scrollY - dropdownHeight - GAP;
        top = Math.max(window.scrollY, idealTop);
      } else {
        top = rect.bottom + window.scrollY;
      }

      this.setState({
        dropdownPosition: {
          top,
          left: rect.left + window.scrollX,
          width: rect.width,
          maxHeight,
          isPositionedAbove,
        },
      });
    }
  };

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.options, this.props.options)) {
      this.setState({ options: this.props.options || [] });
    }
    if (
      !_.isEqual(prevProps.value, this.props.value) &&
      this.props.value !== undefined
    ) {
      const config = this.getEffectiveConfig();
      // Handle object storage (storeFullObjects)
      if (config.features.storeFullObjects) {
        // Convert full objects to IDs for internal use
        const extractedIds = _.map(this.props.value, "value");
        this.setState({
          value: extractedIds,
          originalValues: this.props.value,
        });
      }
      // Standard value handling (IDs only)
      else {
        // Handle single value (convert to array)
        let valueToUse = this.props.value;
        if (_.isString(valueToUse)) {
          valueToUse = [valueToUse];
        }
        this.setState({ value: valueToUse });
      }
    }
  }

  handleClickOutside = event => {
    if (
      this.dropdownRef.current &&
      !this.dropdownRef.current.contains(event.target) &&
      (!this.portalDropdownRef.current ||
        !this.portalDropdownRef.current.contains(event.target))
    ) {
      this.setState({ isOpen: false, searchTerm: "" });
    }
  };

  toggleDropdown = () => {
    const { disabled } = this.props;
    if (!disabled) {
      this.setState(prevState => {
        const newIsOpen = !prevState.isOpen;

        if (newIsOpen) {
          setTimeout(() => {
            if (this.props.isSearchable && this.inputRef.current) {
              this.inputRef.current.focus();
            }
            this.updateDropdownPosition();
          }, 100);
          return { isOpen: true };
        } else {
          return { isOpen: false, searchTerm: "" };
        }
      });
    }
  };

  handleSelectOption = option => {
    const config = this.getEffectiveConfig();
    const { onClick } = this.props;
    // Use 'key' as identifier for hierarchical options
    const optionValue = option.value || option.key;

    // to handle click event from parent component
    if (_.isFunction(onClick)) {
      onClick(option);
    }

    // Check if we need to store full objects
    if (config.features.storeFullObjects) {
      // Special handling for object values
      this.handleObjectValueSelection(option);
      return;
    }

    if (config.mode === "multi") {
      // Multi-select mode
      const newValue = [...this.state.value];
      const index = _.findIndex(newValue, item => item === optionValue);

      // // Toggle selection
      if (index >= 0) {
        newValue.splice(index, 1);
      } else {
        newValue.push(optionValue);
      }

      // Use updateMultiValue pattern for parent communication
      this.updateMultiValue(newValue);
    } else {
      // Single-select mode - always select the clicked option
      // No deselection functionality
      const newValue = [optionValue];
      this.setState({ value: newValue, isOpen: false });
      this.updateSingleValue({ key: optionValue });
    }
  };

  handleObjectValueSelection = option => {
    const { value, originalValues = [] } = this.state;
    const { onChange } = this.props;

    // Clone current value arrays
    const newValue = [...value];
    const newOriginalValues = [...originalValues];

    // Find if this option is already selected
    const index = _.findIndex(newValue, val => val === option.value);
    const originalIndex = _.findIndex(
      newOriginalValues,
      item => item.value === option.value
    );

    if (index >= 0) {
      // Remove from both arrays
      newValue.splice(index, 1);
      if (originalIndex >= 0) {
        newOriginalValues.splice(originalIndex, 1);
      }
    } else {
      // Add to both arrays
      newValue.push(option.value);
      newOriginalValues.push(option);
    }

    // Update internal state
    this.setState({
      value: newValue,
      originalValues: newOriginalValues,
    });

    // Call the onChange prop directly with full objects
    if (onChange) {
      onChange(newOriginalValues);
    }
    this.updateValue(newOriginalValues, { nodes: this.getOptions() });
  };

  updateMultiValue = selectedValues => {
    const options = this.getOptions();

    // Standard multi-select
    const selectedOptions = _.filter(options, option =>
      _.includes(selectedValues, option.value)
    );

    this.updateValue(_.map(selectedOptions, "value"), { nodes: options });
  };

  updateSingleValue = ({ key }) => {
    const { shouldShowArrayValues } = this.props;
    const options = this.getOptions();
    const selectedOption = options.find(opt => opt.value === key);

    let value = selectedOption?.value || "";
    if (shouldShowArrayValues) {
      value = [value];
    }

    this.updateValue(value, {
      nodes: selectedOption ? [selectedOption] : [],
    });
  };

  handleCreateOption = () => {
    const { isCreatable, onChange } = this.props;
    const config = this.getEffectiveConfig();
    const { searchTerm, options, value, originalValues } = this.state;

    if (!searchTerm.trim() || !isCreatable) return;

    // Format new option differently when storing full objects
    let newOption;
    if (config.features.storeFullObjects) {
      // For object values, use the actual searchTerm as both value and label
      newOption = {
        value: searchTerm, // Not lowercase or hyphenated
        label: searchTerm,
      };

      // Call the parent's onCreateOption if provided
      if (this.props.onCreateOption) {
        this.props.onCreateOption(searchTerm);
      }

      const newValue = [...value, newOption.value];
      const newOriginalValues = [...originalValues, newOption];

      // Update state in a single operation
      this.setState({
        options: [...options, newOption],
        value: newValue,
        originalValues: newOriginalValues,
        searchTerm: "", // Clear the search term
      });

      // Notify parent of the change
      if (onChange) {
        onChange(newOriginalValues);
      }

      // FIX: Pass array of IDs (newValue) instead of array of objects
      this.updateValue(newOriginalValues, { nodes: [...options, newOption] });
    } else {
      // Standard format for normal mode
      newOption = {
        value: searchTerm.toLowerCase().replace(/\s+/g, "-"),
        label: searchTerm,
      };

      this.setState(
        prevState => ({
          options: [...prevState.options, newOption],
          searchTerm: "",
        }),
        () => {
          this.handleSelectOption(newOption);
        }
      );
    }
  };

  handleSelectAll = () => {
    const config = this.getEffectiveConfig();
    const { onChange } = this.props;
    const { value, originalValues } = this.state;
    const filteredOptions = this.getFilteredOptions();

    if (config.features.storeFullObjects) {
      // Get values for all filtered options
      const filteredOptionValues = _.map(filteredOptions, "value");

      // Check if all filtered options are currently selected
      const allSelected = _.every(filteredOptionValues, optValue =>
        _.includes(value, optValue)
      );

      // New arrays for IDs and original objects
      let newValues = [...value];
      let newOriginalValues = [...(originalValues || [])];

      if (allSelected) {
        // Deselect all filtered options
        newValues = _.difference(value, _.map(filteredOptions, "value"));
        newOriginalValues = _.differenceBy(
          originalValues,
          filteredOptions,
          "value"
        );
      } else {
        // Select all filtered options
        // Add values that don't already exist
        const newSelections = _.differenceBy(
          filteredOptions,
          originalValues,
          "value"
        );
        newSelections.forEach(option => {
          newValues.push(option.value);
          newOriginalValues.push(option);
        });
      }

      // Update internal state
      this.setState({
        value: newValues,
        originalValues: newOriginalValues,
      });

      // Call onChange prop directly
      if (onChange) {
        onChange(newOriginalValues);
      }

      // FIX: Pass array of IDs (newValues) instead of array of objects
      this.updateValue(newOriginalValues, { nodes: this.getOptions() });
    } else {
      // Original implementation for standard mode
      const filteredOptionValues = _.map(filteredOptions, "value");
      const allSelected = _.every(filteredOptionValues, optValue =>
        _.includes(value, optValue)
      );

      let newValues;
      if (allSelected) {
        // Deselect all filtered options
        newValues = _.difference(value, filteredOptionValues);
      } else {
        // Select all filtered options
        const valuesToAdd = _.difference(filteredOptionValues, value);
        newValues = [...value, ...valuesToAdd];
      }

      this.setState({ value: newValues });
      this.updateMultiValue(newValues);
    }
  };

  handleRemoveOption = (e, option) => {
    e.stopPropagation(); // Prevent dropdown from toggling
    const config = this.getEffectiveConfig();
    const { onChange } = this.props;

    if (config.features.storeFullObjects) {
      // Get the option ID to remove
      const optionId = option.value;
      const { value, originalValues = [] } = this.state;

      // Remove from value array (IDs)
      const newValue = _.filter(value, val => val !== optionId);

      // Remove from originalValues array (full objects)
      const newOriginalValues = _.filter(
        originalValues,
        item => item.value !== optionId
      );

      // Update state
      this.setState({
        value: newValue,
        originalValues: newOriginalValues,
      });

      // Call onChange prop directly
      if (onChange) {
        onChange(newOriginalValues);
      }

      // FIX: Pass array of IDs (newValue) instead of array of objects
      this.updateValue(newOriginalValues, { nodes: this.getOptions() });
    } else {
      // Original implementation for standard mode
      const newValues = _.filter(this.state.value, val => val !== option.value);

      this.setState({ value: newValues });
      this.updateMultiValue(newValues);
    }
  };

  handleSearchChange = e => {
    this.setState({ searchTerm: e.target.value });
  };

  // Helper methods
  getAvailableOptions = () => {
    const { showCheckbox, showTag } = this.props;
    const { options, value } = this.state;

    return showTag && !showCheckbox
      ? _.filter(options, option => !_.includes(value, option.value))
      : options;
  };

  getFilteredOptions = () => {
    const { isSearchable } = this.props;
    const { searchTerm } = this.state;

    const availableOptions = this.getAvailableOptions();

    // If search is enabled and we have a search term
    if (isSearchable && searchTerm) {
      // For hierarchical options
      return _.reduce(
        availableOptions,
        (result, option) => {
          // If option has child options (is a parent)
          if (option.isItemGroup && option.options) {
            // Check if parent label matches search
            const parentMatches = _.includes(
              option.label.toLowerCase(),
              searchTerm.toLowerCase()
            );

            // Filter children that match search
            const matchingChildren = _.filter(option.options, childOption =>
              _.includes(
                childOption.label.toLowerCase(),
                searchTerm.toLowerCase()
              )
            );

            // If parent matches or any children match, include this group
            if (parentMatches || matchingChildren.length > 0) {
              // Create a copy of parent with filtered children
              result.push({
                ...option,
                options: parentMatches ? option.options : matchingChildren,
              });
            }
          }
          // For flat options (backward compatibility)
          else if (
            _.includes(option.label.toLowerCase(), searchTerm.toLowerCase())
          ) {
            result.push(option);
          }
          return result;
        },
        []
      );
    }

    return availableOptions;
  };

  shouldShowCreateOption = () => {
    const { isCreatable } = this.props;
    const { searchTerm, options } = this.state;
    if (!isCreatable) return false;
    const exactMatchExists = _.some(
      options,
      option => _.toLower(option.label) === _.toLower(searchTerm)
    );

    return isCreatable && _.trim(searchTerm) !== "" && !exactMatchExists;
  };

  areAllSelected = () => {
    const { value } = this.state;
    const filteredOptions = this.getFilteredOptions();

    return (
      !_.isEmpty(filteredOptions) &&
      _.every(filteredOptions, option => _.includes(value, option.value))
    );
  };

  areSomeSelected = () => {
    const { value } = this.state;
    const filteredOptions = this.getFilteredOptions();

    return (
      !_.isEmpty(filteredOptions) &&
      _.some(filteredOptions, option => _.includes(value, option.value)) &&
      !this.areAllSelected()
    );
  };

  getOptions = () => {
    const { options } = this.props;

    return _.map(options, option => {
      return {
        ...option,
        key:
          _.get(option, "key") || _.get(option, "id") || _.get(option, "value"),
      };
    });
  };

  getTooltipValue = () => {
    const { multi, labels } = this.props;
    const { value } = this.state;
    const options = this.getOptions();
    if (labels && !_.isEmpty(labels)) {
      return <div>{labels}</div>;
    }
    // Only show tooltip for multi-select with more than 2 items
    if (multi && value.length > 1) {
      return (
        <div className={classes.tooltipContent}>
          {_.map(value, optionValue => {
            // Find the full option object to get the label
            const option = _.find(options, { value: optionValue });
            return (
              <div key={optionValue}>{option ? option.label : optionValue}</div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  displayValue = () => {
    const { multi, placeholder, t, showTag, labels } = this.props;
    const config = this.getEffectiveConfig();
    const { value, isOpen, originalValues, options } = this.state;
    if (!value || value.length === 0) {
      return <div className={classNames("text-body")}>{placeholder}</div>;
    }

    if (!_.isArray(value)) {
      return value;
    }

    if (!multi && value.length === 1) {
      const selectedKey = value[0];

      // Look through all options and their children to find the match
      for (const parentOption of options) {
        if (parentOption.isItemGroup && parentOption.options) {
          const childOption = _.find(parentOption.options, {
            key: selectedKey,
          });
          if (childOption) {
            return childOption.label;
          }
        } else if (
          parentOption.key === selectedKey ||
          parentOption.value === selectedKey
        ) {
          return parentOption.label;
        }
      }
    }
    if (labels) {
      return (
        <div className={classNames("text-body", "truncate")}>{labels}</div>
      );
    }

    if (config.features.storeFullObjects) {
      // Use the original objects for display
      if (isOpen) {
        const { hideDropdownIndicator } = this.props;
        return (
          <div
            className={classNames(classes.selectedChips, {
              [classes.withIndicator]: !hideDropdownIndicator,
            })}
          >
            {_.map(value, optionValue => {
              // Find the matching object in originalValues or options
              const originalOption =
                _.find(originalValues, { value: optionValue }) ||
                _.find(options, { value: optionValue });

              return (
                <div key={optionValue} className={classes.chip}>
                  <span className={classNames(classes.chipLabel, "text-body")}>
                    {_.get(originalOption, "label", optionValue)}
                  </span>
                  <CloseOutlined
                    size="xxx-small"
                    className={classes.chipRemove}
                    dsVersion="2.0"
                    onClick={e =>
                      this.handleRemoveOption(
                        e,
                        originalOption || { value: optionValue }
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        );
      }
      const returnValue = getLabelValueForDropdown({
        selectedItems: value || [],
        itemList: !_.isEmpty(options)
          ? options
          : _.map(originalValues || [], val => {
              return {
                label: val?.label || val,
                value: val?.value || val,
              };
            }),
        noneSelectedText: !_.isEmpty(placeholder)
          ? placeholder
          : t("common:select"),
        showLabelsCount: 1,
        separator: t("common:and"),
        showAllText: false,
        moreLabel: "common:more_lowercase",
        t,
      });
      return returnValue;
    }
    if (isOpen && showTag) {
      const { hideDropdownIndicator } = this.props;
      return (
        <div
          className={classNames(classes.selectedChips, {
            [classes.withIndicator]: !hideDropdownIndicator,
          })}
        >
          {_.map(value, optionValue => {
            // Find the matching object in originalValues or options
            const originalOption = _.find(options, { value: optionValue });

            return (
              <div key={optionValue} className={classes.chip}>
                <span className={classes.chipLabel}>
                  {_.get(originalOption, "label", optionValue)}
                </span>
                <CloseOutlined
                  size="xxx-small"
                  className={classes.chipRemove}
                  dsVersion="2.0"
                  onClick={e =>
                    this.handleRemoveOption(
                      e,
                      originalOption || { value: optionValue }
                    )
                  }
                />
              </div>
            );
          })}
        </div>
      );
    }

    // Rest of the method remains unchanged
    const hasReactElement = _.some(value, valueId => {
      const option = _.find(options, { value: valueId });
      return option && React.isValidElement(option.label);
    });

    // If any selected option has React element as label, show simple comma-separated format
    if (hasReactElement) {
      return (
        <div className={classes.reactElementContainer}>
          {_.map(value, valueId => {
            const option = _.find(options, { value: valueId });
            if (!option) return null;

            return <div key={valueId}>{option.label}</div>;
          })}
        </div>
      );
    }

    if (_.isArray(value)) {
      return (
        <div
          className={classNames("text-body", {
            [classes.buttonClass]: true,
          })}
        >
          {getLabelValueForDropdown({
            selectedItems: value,
            itemList: !_.isEmpty(options)
              ? options
              : _.map(value, val => {
                  return {
                    label: val,
                    value: val,
                  };
                }),
            noneSelectedText: !_.isEmpty(placeholder)
              ? placeholder
              : t("common:select"),
            showLabelsCount: 1,
            separator: t("common:and"),
            showAllText: false,
            moreLabel: "common:more_lowercase",
            t,
          })}
        </div>
      );
    }

    // Special case: multi-select WITHOUT CHECKBOXES with open dropdown
    if (showTag) {
      return (
        <div className={classes.selectedChips}>
          {_.map(value, option => (
            <div key={option.value} className={classes.chip}>
              <span className={classes.chipLabel}>{option.label}</span>
              <CloseOutlined
                size="xxx-small"
                className={classes.chipRemove}
                dsVersion="2.0"
                onClick={e => this.handleRemoveOption(e, option)}
              />
            </div>
          ))}
        </div>
      );
    }
  };

  areOptionSelected = option => {
    const { value } = this.state;
    return _.includes(value, option.value);
  };

  getRelatedConceptsFilteredOptions = () => {
    const { searchTerm } = this.state;
    const availableOptions = this.getAvailableOptions();

    // Filter by search term if available
    return searchTerm
      ? _.filter(availableOptions, option =>
          _.includes(_.toLower(option.label), _.toLower(searchTerm))
        )
      : availableOptions;
  };

  // UIBaseComponent override methods
  renderDropdownContent = () => {
    const {
      multi,
      isSelectAllEnabled,
      showCheckbox,
      isSearchable,
      isCreatable,
      t,
      emptyText,
    } = this.props;
    const { searchTerm } = this.state;

    const filteredOptions = this.getFilteredOptions();
    const shouldShowCheckbox = multi && showCheckbox;
    const shouldShowCreateOption = this.shouldShowCreateOption();
    const placeHolderText =
      isSearchable && isCreatable
        ? t("common:search_or_create_new_option")
        : t("common:search");

    return (
      <>
        {isSearchable && (
          <div className={classes.searchContainer}>
            <SearchOutlined
              size="xx-small"
              dsVersion="2.0"
              className={classes.searchIcon}
            />
            <input
              ref={this.inputRef}
              type="text"
              placeholder={placeHolderText}
              value={searchTerm}
              onChange={this.handleSearchChange}
              className={classNames(classes.searchInput, "text-body", {
                [classes.showPlaceHolder]: _.isEmpty(searchTerm),
              })}
            />
          </div>
        )}

        {shouldShowCreateOption && (
          <div
            className={classNames(classes.createOption, "text-label")}
            onClick={this.handleCreateOption}
            role="button"
            tabIndex={0}
            onKeyDown={e =>
              (e.key === "Enter" || e.key === " ") && this.handleCreateOption()
            }
          >
            <AddCircleOutlined
              size="xx-small"
              className={classes.createIcon}
              dsVersion="2.0"
            />
            <span>
              {t("common:create")} &quot;{searchTerm}&quot;
            </span>
          </div>
        )}

        <div className={classes.optionsList}>
          {multi &&
            isSelectAllEnabled &&
            !_.isEmpty(filteredOptions) &&
            _.isEmpty(searchTerm) &&
            _.size(filteredOptions) > 5 && (
              <div
                className={classNames(classes.option, {
                  [classes.selected]:
                    this.areAllSelected() || this.areSomeSelected(),
                })}
                onClick={this.handleSelectAll}
                role="option"
                aria-selected={this.areAllSelected()}
                tabIndex={0}
                onKeyDown={e =>
                  (e.key === "Enter" || e.key === " ") && this.handleSelectAll()
                }
              >
                {shouldShowCheckbox && (
                  <div
                    className={classNames(classes.checkboxContainer, {
                      [classes.checked]:
                        this.areAllSelected() || this.areSomeSelected(),
                      [classes.intermediate]: this.areSomeSelected(),
                    })}
                  >
                    {this.areAllSelected() && (
                      <TickSmallOutlined
                        size="xx-small"
                        className={classes.tickIcon}
                        dsVersion="2.0"
                      />
                    )}
                    {this.areSomeSelected() && (
                      <MinusOutlined
                        size="xxx-small"
                        className={classes.tickIcon}
                        dsVersion="2.0"
                      />
                    )}
                  </div>
                )}
                <span
                  className={classNames(classes.optionLabel, "text-body", {
                    [classes.selectAllLabel]: !showCheckbox && multi,
                  })}
                >
                  {!showCheckbox && multi
                    ? t("common:select_all")
                    : t("common:all")}
                </span>
              </div>
            )}

          {!_.isEmpty(filteredOptions) ? (
            _.flatMap(filteredOptions, (option, index) => {
              // Handle hierarchical options
              if (option.isItemGroup && option.options) {
                const elements = [];

                // Add parent option (non-clickable)
                elements.push(
                  <div
                    key={`parent-${option.key}`}
                    className={classNames(classes.option, classes.parentOption)}
                  >
                    <span
                      className={classNames(classes.optionLabel, "text-label")}
                    >
                      {option.label}
                    </span>
                  </div>
                );

                // Add child options (clickable)
                option.options.forEach(childOption => {
                  const isSelected = _.some(
                    this.state.value,
                    item => item === childOption.key
                  );

                  elements.push(
                    <div
                      key={childOption.key}
                      className={classNames(classes.option, {
                        [classes.selected]: isSelected,
                      })}
                      onClick={() => this.handleSelectOption(childOption)}
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={0}
                      onKeyDown={e =>
                        (e.key === "Enter" || e.key === " ") &&
                        this.handleSelectOption(childOption)
                      }
                    >
                      {shouldShowCheckbox && (
                        <div
                          className={classNames(classes.checkboxContainer, {
                            [classes.checked]: isSelected,
                          })}
                        >
                          {isSelected && (
                            <TickSmallOutlined
                              size="xx-small"
                              className={classes.tickIcon}
                              dsVersion="2.0"
                            />
                          )}
                        </div>
                      )}
                      <span
                        className={classNames(classes.optionLabel, "text-body")}
                      >
                        {childOption.label}
                      </span>
                    </div>
                  );
                });

                // Add divider after the group
                if (index < filteredOptions.length - 1) {
                  elements.push(
                    <div
                      key={`divider-${option.key}`}
                      className={classes.divider}
                    />
                  );
                }

                return elements;
              }
              // Handle regular options for backward compatibility
              else {
                const isSelected = _.some(
                  this.state.value,
                  item => item === option.value || item === option.key
                );

                // Check if this option should be rendered as a divider
                if (option.label === "" && option.isDivider === true) {
                  return (
                    <div
                      key={option.value || option.key}
                      className={classes.divider}
                    />
                  );
                }

                return (
                  <div
                    key={option.value || option.key}
                    className={classNames(classes.option, {
                      [classes.selected]: isSelected,
                    })}
                    onClick={() => this.handleSelectOption(option)}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    onKeyDown={e =>
                      (e.key === "Enter" || e.key === " ") &&
                      this.handleSelectOption(option)
                    }
                  >
                    {shouldShowCheckbox && (
                      <div
                        className={classNames(classes.checkboxContainer, {
                          [classes.checked]: isSelected,
                        })}
                      >
                        {isSelected && (
                          <TickSmallOutlined
                            size="xx-small"
                            className={classes.tickIcon}
                            dsVersion="2.0"
                          />
                        )}
                      </div>
                    )}
                    <span
                      className={classNames(classes.optionLabel, "text-body")}
                    >
                      {option.label}
                    </span>
                  </div>
                );
              }
            })
          ) : // Empty state handling
          _.isEmpty(searchTerm) && isCreatable ? (
            <div className={classes.emptyState}>
              <SearchOutlined
                size="x-small"
                dsVersion="2.0"
                className={classes.emptyStateIcon}
              />
              <div className={classNames(classes.emptyStateText, "text-body")}>
                {emptyText || "no option"}
              </div>
            </div>
          ) : (
            // Search with no results
            isSearchable &&
            !_.isEmpty(searchTerm) &&
            !shouldShowCreateOption && (
              <div className={classNames(classes.noOptions, "text-body")}>
                No options found
              </div>
            )
          )}
        </div>
      </>
    );
  };

  renderDropdown = () => {
    const { isOpen, dropdownPosition } = this.state;

    if (!isOpen) return null;

    const dropdownStyle = {
      position: "absolute",
      top: `${dropdownPosition.top}px`,
      left: `${dropdownPosition.left}px`,
      width: `${dropdownPosition.width}px`,
      maxHeight: `${dropdownPosition.maxHeight}px`,
      overflow: "auto",
      zIndex: 2000,
    };

    const dropdownContent = (
      <div
        className={classes.content}
        ref={this.portalDropdownRef}
        style={dropdownStyle}
      >
        {this.renderDropdownContent()}
      </div>
    );

    return ReactDOM.createPortal(dropdownContent, document.body);
  };

  renderButton = () => {
    const {
      disabled = false,
      buttonStyle,
      multi,
      value,
      options,
      placeholder,
    } = this.props;
    const selectedValue = value[0];
    const selectedOption = _.find(
      options,
      option => option.value === selectedValue || option.key === selectedValue
    );
    const displayLabel = selectedOption ? selectedOption.label : placeholder;
    const className = classNames(classes.dsButtonClass, {
      [classes.fullWidth]: true,
      [classes.disabledDSButtonClass]: disabled,
    });
    return (
      <Button
        ref={this.buttonRef}
        dsVersion="2.0"
        _legacy_variant="plain"
        size="small"
        variant="neutral"
        type="plain"
        shouldStopPropagation={true}
        className={className}
        disabled={disabled}
        style={buttonStyle}
      >
        <Tooltip
          tooltip={this.getTooltipValue()}
          placement="top"
          visible={multi && value.length > 1}
          showArrow={true}
          dsVersion="2.0"
          overlayInnerStyle={styles.overlayInnerStyle}
        >
          {multi ? (
            this.displayValue()
          ) : (
            <div
              className={classNames("text-body", {
                [classes.buttonClass]: true,
                [classes.placeholder]: !selectedOption,
              })}
            >
              {displayLabel}
            </div>
          )}
        </Tooltip>
      </Button>
    );
  };

  renderChecklistDropdown = () => {
    const {
      value,
      disabled = false,
      overlayStyle = {},
      getPopupContainer,
      testId,
      label,
      onChange,
      options,
    } = this.props;
    const checklistDropdownClass = classNames("text-body", {
      [classes.fullWidth]: true,
      [classes.overlayWithError]: this.state.error,
      [classes.container]: true,
      [classes.showPlaceHolder]: !value || value.length === 0,
    });
    return (
      <div className={checklistDropdownClass}>
        <ChecklistDropdown
          dsVersion="2.0"
          showAllOption={true}
          options={options}
          value={value || []}
          onChange={onChange}
          className={checklistDropdownClass}
          style={overlayStyle}
          disabled={disabled}
          showSelectedOptionsOnTop={false}
          getPopupContainer={getPopupContainer}
          testId={`${testId}-${_.camelCase(label)}`}
        >
          {this.renderButton()}
        </ChecklistDropdown>
      </div>
    );
  };

  renderSingleSelectDropdown = () => {
    const {
      disabled,
      overlayStyle,
      getPopupContainer,
      testId,
      label,
      options,
      onClick,
    } = this.props;
    const overlayClassName = classNames({
      [classes.fullWidth]: true,
      [classes.overlayWithError]: this.state.error,
    });

    const dropdownContainerClass = classNames("text-body", {
      [classes.fullWidth]: true,
      [classes.container]: true,
    });

    return (
      <div className={dropdownContainerClass}>
        <Dropdown
          overlay={
            <DropdownMenu
              dsVersion="2.0"
              options={options}
              onClick={onClick}
              shouldStopPropagation={true}
              testId={`${testId}-${_.camelCase(label)}`}
              hasMultilineOptions={true}
            />
          }
          disabled={disabled}
          overlayClassName={overlayClassName}
          overlayStyle={overlayStyle}
          getPopupContainer={getPopupContainer}
        >
          {this.renderButton()}
        </Dropdown>
      </div>
    );
  };

  renderEdit = () => {
    const {
      hideDropdownIndicator,
      multi,
      disabled,
      showTag,
      isCreatable,
      isSearchable,
      isCell = false,
    } = this.props;
    const { isOpen, value } = this.state;
    if (multi && !showTag && !isCreatable && !isSearchable) {
      return this.renderChecklistDropdown();
    } else if (!multi && !showTag && !isCreatable && !isSearchable) {
      return this.renderSingleSelectDropdown();
    } else {
      return (
        <div
          className={classNames(classes.container, {
            [classes.cellContainer]: isCell,
          })}
          ref={this.dropdownRef}
        >
          <Button
            ref={this.buttonRef}
            onClick={this.toggleDropdown}
            variant="neutral"
            type="plain"
            size="small"
            shouldStopPropagation={true}
            className={classNames(classes.dropdownButton, {
              [classes.active]: isOpen,
              [classes.hasChips]: showTag && value.length > 0 && isOpen,
              [classes.cell]: isCell,
            })}
            disabled={disabled}
            dsVersion="2.0"
          >
            <Tooltip
              tooltip={this.getTooltipValue()}
              placement="top"
              visible={!isOpen && multi && value.length > 1}
              showArrow={true}
              dsVersion="2.0"
              overlayInnerStyle={styles.overlayInnerStyle}
            >
              <div className={classes.buttonContent}>
                <div
                  className={classNames(classes.selectedValues, "text-body", {
                    [classes.chipContainer]:
                      showTag && value.length > 0 && isOpen,
                    [classes.showPlaceHolder]: value.length === 0,
                    [classes.withIndicator]: !hideDropdownIndicator,
                  })}
                >
                  {this.displayValue()}
                </div>
                {!hideDropdownIndicator && (
                  <div className={classes.arrow}>
                    {isOpen ? (
                      <CaretUpFilled size="xx-small" />
                    ) : (
                      <CaretDownFilled size="xx-small" />
                    )}
                  </div>
                )}
              </div>
            </Tooltip>
          </Button>

          {this.renderDropdown()}
        </div>
      );
    }
  };

  renderView = () => {
    const { value } = this.state;
    const { multi } = this.props;

    return (
      <Tooltip
        tooltip={this.getTooltipValue()}
        placement="top"
        visible={!this.state.isOpen && multi && value.length > 1}
        showArrow={true}
        dsVersion="2.0"
        align={{ offset: [0, 0] }}
        overlayInnerStyle={styles.overlayInnerStyle}
      >
        <div className={classNames(classes.viewContainer, "text-body")}>
          {this.displayValue()}
        </div>
      </Tooltip>
    );
  };

  shouldShowViewEmpty = () => {
    const { value } = this.state;
    return _.isEmpty(value);
  };
}

MultiSelectDropdown.defaultProps = {
  ...UIBaseComponent.defaultProps,
  options: [],
  onChange: () => {},
  placeholder: "Select option",
  disabled: false,
  isCreatable: false,
  isSearchable: false,
  multi: true,
  isSelectAllEnabled: true,
  hideDropdownIndicator: true,
  showCheckbox: true,
  storeObjectValues: false,
  showTag: false,
  emptyText: "No options available",
};

// Store the static Variants before HOC wrapping
const DropdownVariants = MultiSelectDropdown.Variants;

// Apply HOCs
const EnhancedMultiSelectDropdown = compose(I18nHOC({ resource: ["common"] }))(
  MultiSelectDropdown
);

// Re-attach static properties to the wrapped component
EnhancedMultiSelectDropdown.Variants = DropdownVariants;

export default EnhancedMultiSelectDropdown;
